import { yupResolver } from '@hookform/resolvers/yup'
import { useEffect, useMemo, useRef, useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { Controller, useForm } from 'react-hook-form'
import { useIntl } from 'react-intl'
import { Link, useSearchParams } from 'react-router-dom'
import * as yup from 'yup'

import AlertModal from '@/app/components/AlertModal/AlertModal'
import { ErrorMessage } from '@/app/components/ErrorMessage'
import { Input } from '@/app/components/Input'
import { OutlineButton } from '@/app/components/OutlineButton'
import { PrimaryButton } from '@/app/components/PrimaryButton'
import { useAuthRegister } from '@/app/hooks/auth/useAuthRegister'
import { GoogleLogo } from '@/app/icons/GoogleLogo'
import { MicrosoftLogo } from '@/app/icons/MicrosoftLogo'
import { LanguageSelector } from '@/app/components/LanguageSelector/LanguageSelector'
import { MydraDuoLogo } from '@/app/icons/MydraDuoLogo/MydraDuoLogo'
import { isValidRedirect } from '@/app/lib/auth'
import { API_URL, EXTERNAL_LINKS, ROUTES } from '@/app/lib/constants'
import { getErrorMessage } from '@/app/lib/getErrorMessage'
import {
  sendSignUpPageViewEvent,
  sendUserSignUpEvent,
} from '@/app/modules/shared/lib/analytics'
import { UserRole } from '@/types/user'
import * as CookieConsent from 'vanilla-cookieconsent'
import { SignUpBenefitsEdpCampaign } from '../../components/SignUpBenefitsEdpCampaign'
import { Typography } from '@/app/components/Typography'

type FormValues = {
  firstName: string
  lastName: string
  email: string
}

export function SignUpPage() {
  const intl = useIntl()
  const [searchParams] = useSearchParams()
  const wasAnalyticsSent = useRef(false)

  const formSchema = yup.object<FormValues>().shape({
    firstName: yup.string().required(
      intl.formatMessage({
        id: 'SignUp.validation_required',
        defaultMessage: 'This field is required',
      })
    ),
    lastName: yup.string().required(
      intl.formatMessage({
        id: 'SignUp.validation_required',
        defaultMessage: 'This field is required',
      })
    ),
    email: yup
      .string()
      .email()
      .required(
        intl.formatMessage({
          id: 'SignUp.validation_required',
          defaultMessage: 'This field is required',
        })
      ),
  })

  const invitation = searchParams.get('invitation') ?? ''
  const product = searchParams.get('product') ?? ''
  const campaign = searchParams.get('campaign') ?? ''
  const flow = searchParams.get('flow') ?? ''

  const { email: invitedEmail, role } = useMemo(() => {
    if (!invitation) {
      return {}
    }

    const [, invitationRawPayload] = invitation?.split('.') ?? []

    try {
      return JSON.parse(window.atob(invitationRawPayload))
    } catch {
      return {}
    }
  }, [invitation])

  const {
    handleSubmit,
    control,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: yupResolver(formSchema),
    values: {
      firstName: '',
      lastName: '',
      email: invitedEmail ?? '',
    },
  })

  const [serverResponse, setServerResponse] = useState<{
    message: string
    type: 'success' | 'error'
  }>({
    message: '',
    type: 'success',
  })
  const [isRedirectingToExternalLogin, setIsRedirectingToExternalLogin] =
    useState(false)
  const [isAlertModalOpen, setIsAlertModalOpen] = useState(false)
  const { mutateAsync: registerPasswordLessUser } = useAuthRegister()
  const redirectTo = searchParams.get('redirectTo')
  const companyCode = searchParams.get('companyCode') ?? ''

  const onboardingUrl = useMemo(() => {
    // Education provider onboarding still lives in the marketplace.
    if (role === UserRole.EducationProvider) {
      const url = new URL(
        '/onboarding/education-provider',
        EXTERNAL_LINKS.marketplace
      )

      if (product) {
        url.searchParams.append('product', product)
      }

      if (redirectTo && isValidRedirect(redirectTo)) {
        url.searchParams.append('redirectTo', redirectTo)
      }

      return url.toString()
    }

    // User onboarding is now hosted in auth-web. The AI-business wizard is the
    // default flow. The basic-info flow is used when explicitly requested via
    // `flow=basic-info` (e.g. forwarded by the marketplace) or the EDP campaign.
    const url = new URL(ROUTES.onboarding, window.location.origin)

    if (flow === 'basic-info' || campaign === 'edp') {
      url.searchParams.append('flow', 'basic-info')
    }

    if (redirectTo && isValidRedirect(redirectTo)) {
      url.searchParams.append('redirectTo', redirectTo)
    }

    return url.toString()
  }, [redirectTo, role, product, campaign, flow])

  const formRef = useRef<HTMLFormElement>(null)

  const handleOnSubmit = async (data: FormValues) => {
    try {
      await registerPasswordLessUser({
        ...data,
        redirectTo: onboardingUrl,
        companyCode,
        invitation,
      })

      setServerResponse({
        message: intl.formatMessage({
          id: 'SignUp.email_verification_message',
          defaultMessage: 'Check your email for verification',
        }),
        type: 'success',
      })

      sendUserSignUpEvent()
      reset()
    } catch {
      reset(
        {},
        {
          keepValues: true,
          keepErrors: true,
        }
      )
      setServerResponse({
        message: intl.formatMessage({
          id: 'SignUp.email_verification_error',
          defaultMessage: 'Error sending verification email',
        }),
        type: 'error',
      })
    } finally {
      if (!isAlertModalOpen) {
        setIsAlertModalOpen(true)
      }
    }
  }

  const redirectToGoogleLogin = async () => {
    setIsRedirectingToExternalLogin(true)

    await sendUserSignUpEvent(() => {
      const redirectUrl = `${API_URL}/auth/google?redirectUrl=${encodeURIComponent(onboardingUrl)}`
      window.location.href = redirectUrl
    })
  }

  const redirectToMicrosoftLogin = async () => {
    setIsRedirectingToExternalLogin(true)

    await sendUserSignUpEvent(() => {
      const redirectUrl = `${API_URL}/auth/microsoft?redirectUrl=${encodeURIComponent(onboardingUrl)}`
      window.location.href = redirectUrl
    })
  }

  useEffect(() => {
    if (!wasAnalyticsSent.current) {
      sendSignUpPageViewEvent()
      wasAnalyticsSent.current = true
    }
  }, [])

  return (
    <>
      <Helmet>
        <title>Sign up - Mydra</title>
        <style>
          {` .mydra-base-layout { min-height: 0; height: 100vh; } `}
        </style>
      </Helmet>
      <div className="flex relative flex-col h-full md:flex-row">
        <div className="absolute right-4 top-4 z-20">
          <LanguageSelector />
        </div>
        <div className="flex flex-col gap-8 justify-center items-center p-8 text-center md:w-1/2 md:flex">
          {campaign === 'edp' ? (
            <>
              <img
                src="/images/edp-powered-by-mydra.png"
                alt="EDP Powered by Mydra"
                className="md:block hidden mx-auto mt-12 h-10 w-auto md:mt-7 md:ml-9"
                style={{
                  imageRendering: 'auto',
                }}
                width={500}
                height={84}
              />
              <img
                src="/images/edp-powered-by-mydra.png"
                alt="EDP Powered by Mydra"
                className="md:hidden mx-auto mt-12 h-10 w-auto md:mt-7 md:ml-9"
                style={{
                  imageRendering: 'auto',
                }}
                width={500}
                height={84}
              />
            </>
          ) : (
            <MydraDuoLogo className="mx-auto mt-12 h-8 md:mt-7 md:ml-9" />
          )}

          <div className="flex flex-col w-full bg-white md:w-96 md:my-auto md:mx-auto">
            <form
              className="container self-stretch md:px-0 md:py-12"
              onSubmit={handleSubmit(handleOnSubmit)}
              ref={formRef}
            >
              <div className="flex flex-col">
                <div className="space-y-2">
                  <Typography variant="h1">
                    {intl.formatMessage({
                      id: 'SignUp.create_your_account',
                      defaultMessage: 'Create your account',
                    })}
                  </Typography>
                  <Typography variant="body1">
                    {intl.formatMessage({
                      id: 'SignUp.get_started_in_a_few_seconds',
                      defaultMessage: 'Get started in a few seconds',
                    })}
                  </Typography>
                </div>
                <div className="flex flex-col gap-4 mt-6">
                  <Controller
                    control={control}
                    name="email"
                    render={({ field }) => (
                      <Input
                        {...field}
                        type="email"
                        id="email"
                        placeholder={intl.formatMessage({
                          id: 'SignUp.email_placeholder',
                          defaultMessage: 'Email',
                        })}
                        errorMessage={getErrorMessage('email', errors)}
                        disabled={!!invitedEmail}
                      />
                    )}
                  />
                  <div className="flex gap-4 justify-between w-full md:flex-row">
                    <div className="flex-[1]">
                      <Controller
                        control={control}
                        name="firstName"
                        render={({ field }) => (
                          <Input
                            {...field}
                            type="text"
                            id="firstName"
                            placeholder={intl.formatMessage({
                              id: 'SignUp.first_name_placeholder',
                              defaultMessage: 'First name',
                            })}
                            errorMessage={getErrorMessage('firstName', errors)}
                            className="w-full"
                          />
                        )}
                      />
                    </div>
                    <div className="flex-[1]">
                      <Controller
                        control={control}
                        name="lastName"
                        render={({ field }) => (
                          <Input
                            {...field}
                            type="text"
                            id="lastName"
                            placeholder={intl.formatMessage({
                              id: 'SignUp.last_name_placeholder',
                              defaultMessage: 'Last name',
                            })}
                            errorMessage={getErrorMessage('lastName', errors)}
                            className="w-full"
                          />
                        )}
                      />
                    </div>
                  </div>

                  <div className="flex flex-col gap-4">
                    <PrimaryButton className="w-full" isLoading={isSubmitting}>
                      {intl.formatMessage({
                        id: 'SignUp.continue',
                        defaultMessage: 'Continue',
                      })}
                    </PrimaryButton>
                    <div className="text-sm text-mydra-black">
                      {intl.formatMessage(
                        {
                          id: 'SignUp.already_have_account_login',
                          defaultMessage: 'Already have an account? {link}',
                        },
                        {
                          link: (
                            <Link
                              className="font-semibold"
                              to={{
                                pathname: ROUTES.login,
                                search: location.search,
                              }}
                            >
                              {intl.formatMessage({
                                id: 'SignUp.log_in',
                                defaultMessage: 'Login',
                              })}
                            </Link>
                          ),
                        }
                      )}
                    </div>
                  </div>
                  <hr className="mt-4 mb-2 hr-text" data-content="OR" />
                  <OutlineButton
                    className="w-full !text-sm font-semibold"
                    onClick={(e) => {
                      e.preventDefault()
                      redirectToGoogleLogin()
                    }}
                    disabled={isRedirectingToExternalLogin}
                  >
                    <div className="flex items-center w-full">
                      <div className="flex-none w-6 ml-2 mr-4 flex justify-center">
                        <GoogleLogo />
                      </div>
                      <div className="flex-1 text-center">
                        {intl.formatMessage({
                          id: 'SignUp.continueWithGoogle',
                          defaultMessage: 'Continue with Google',
                        })}
                      </div>
                      <div className="flex-none w-6 mr-2"></div>{' '}
                      {/* Spacer for balance */}
                    </div>
                  </OutlineButton>
                  <OutlineButton
                    className="w-full !text-sm font-semibold"
                    onClick={(e) => {
                      e.preventDefault()
                      redirectToMicrosoftLogin()
                    }}
                    disabled={isRedirectingToExternalLogin}
                  >
                    <div className="flex items-center w-full">
                      <div className="flex-none w-6 ml-2 mr-4 flex justify-center">
                        <MicrosoftLogo />
                      </div>
                      <div className="flex-1 text-center">
                        {intl.formatMessage({
                          id: 'SignUp.continueWithMicrosoft',
                          defaultMessage: 'Continue with Microsoft',
                        })}
                      </div>
                      <div className="flex-none w-6 mr-2"></div>{' '}
                      {/* Spacer for balance */}
                    </div>
                  </OutlineButton>
                </div>
                <ErrorMessage
                  message={errors.root?.serverError?.message}
                  className="mt-2 text-left"
                />
                <button
                  type="button"
                  className="absolute bottom-0 right-0 p-4 text-sm "
                  onClick={() => {
                    CookieConsent.showPreferences()
                  }}
                >
                  Cookie preferences
                </button>
              </div>
            </form>
          </div>
        </div>
        <div className="hidden relative flex-col gap-8 justify-center items-center p-8 text-center md:w-1/2 md:flex">
          <div className="absolute p-2 w-full h-full">
            {campaign === 'edp' ? (
              <img
                src="/images/edp-background.jpg"
                alt="EDP Sign Up Page Video"
                className="object-cover object-top w-full h-full bg-right-top rounded-xl filter brightness-[65%]"
              />
            ) : (
              <img
                className="object-cover w-full h-full rounded-xl"
                src="/images/static/img/hero_bg.jpg"
                alt="Hero background"
              />
            )}
          </div>
          {campaign === 'edp' && (
            <SignUpBenefitsEdpCampaign className="z-20 w-[410px]" />
          )}
        </div>
        <div className="p-3 pt-8 md:hidden">
          {campaign === 'edp' && (
            <SignUpBenefitsEdpCampaign className="bg-mydra-dark-purple" />
          )}
        </div>
        <AlertModal
          message={serverResponse.message}
          isOpen={isAlertModalOpen}
          onClose={async (result) => {
            if (result) {
              formRef.current?.requestSubmit()
            }
            setIsAlertModalOpen(false)
          }}
        />
      </div>
    </>
  )
}
